/*
 * Copyright (c) 2026-present Amida Technology Solutions, Inc.
 * All rights reserved.
 *
 * Licensed under the Amida proprietary license.
 * See LICENSE file in the project root for license information.
 */

/** The shortest and longest reason the server accepts, after trimming (`LIFE-65`). */
export const REASON_LEAST = 3;
export const REASON_MOST = 500;

/** Said under a reason that is empty or too short, once something was typed. */
export const REASON_TOO_SHORT = `Say why in at least ${String(REASON_LEAST)} characters.`;

/** Said under a reason that runs past the limit. */
export const REASON_TOO_LONG = `Keep it to ${String(REASON_MOST)} characters or fewer.`;

/** Said under a reason carrying a character the server refuses (`PLAIN_NOTE`). */
export const REASON_BAD_CHARACTER =
  'This has a character that cannot be used here. Type it again, or paste it as plain text.';

/**
 * Whether one character is one the server's `PLAIN_NOTE` refuses: a control
 * character other than a tab, a line feed or a carriage return.
 */
function refused(code: number): boolean {
  if (code === 0x09 || code === 0x0a || code === 0x0d) return false;
  return code <= 0x1f || code === 0x7f;
}

/**
 * What is wrong with a reason, by the server's own rule, or `null` when it may
 * be sent. A reason for cancelling or for sending an agreement back: trimmed,
 * 3 to 500 characters, and no control character but a tab or a line break
 * (`LIFE-65`, ADW-5501). The words name the rule, never what was typed.
 */
export function reasonProblem(reason: string): string | null {
  for (const character of reason) {
    if (refused(character.codePointAt(0) ?? 0)) return REASON_BAD_CHARACTER;
  }
  // Counted as the server counts: trimmed, by character rather than by
  // UTF-16 unit.
  const length = Array.from(reason.trim()).length;
  if (length < REASON_LEAST) return REASON_TOO_SHORT;
  if (length > REASON_MOST) return REASON_TOO_LONG;
  return null;
}
